/**
 * Shared Status Type Definitions
 * 
 * Centralized status type definitions to ensure type safety and consistency
 * across the application. Use these types instead of string literals.
 */

// ============================================================================
// Core Entity Statuses
// ============================================================================

/**
 * Lease Status — document lifecycle (Phase 7.2).
 * Use `normalizeLeaseStatus()` at API/UI boundaries if a legacy `Pending Signature` string may appear.
 */
export type LeaseStatus =
  | 'Draft'
  | 'Out_For_Signature'
  | 'Executed'
  | 'Active'
  | 'Expired'
  | 'Terminated'
  | 'Renewed';

/**
 * Property Status
 * Represents the operational state of a property
 */
export type PropertyStatus = 'Stable' | 'At Risk' | 'Lease-up' | 'Renovation' | 'Vacant';

/**
 * Tenant Status
 * Represents the current state of a tenant's residency
 */
export type TenantStatus = 'Current' | 'Notice' | 'Past';

/**
 * Owner Status
 * Represents the current state of a property owner account
 */
export type OwnerStatus = 'Active' | 'Inactive' | 'Pending';

/**
 * Unit Status
 * Represents the current state of a rental unit
 */
export type UnitStatus = 'Vacant' | 'Occupied' | 'Renovation';

// ============================================================================
// Maintenance Statuses
// ============================================================================

/**
 * Work Order Status
 * Represents the current state of a maintenance work order
 */
export type WorkOrderStatus = 'Open' | 'In Progress' | 'Vendor Assigned' | 'Completed' | 'Verified' | 'Pending Approval' | 'Approved' | 'Rejected' | 'Cancelled';

/** Statuses considered "active" for maintenance ticket list (not yet completed/verified/cancelled). Keep in sync with API getTickets(statusFilter === 'active'). */
export const TICKET_STATUS_ACTIVE: readonly WorkOrderStatus[] = ['Open', 'In Progress', 'Vendor Assigned', 'Pending Approval', 'Approved', 'Rejected'] as const;

/** Statuses considered "completed" for maintenance ticket list. Keep in sync with API getTickets(statusFilter === 'completed'). */
export const TICKET_STATUS_COMPLETED: readonly WorkOrderStatus[] = ['Completed', 'Verified', 'Cancelled'] as const;

/** Tab value for maintenance view: active vs completed. Backend accepts status filter 'active' | 'completed' (or a single WorkOrderStatus). */
export type MaintenanceViewTab = 'active' | 'completed';

/**
 * Inspection Status
 * Represents the current state of a property inspection
 */
export type InspectionStatus = 'Scheduled' | 'In Progress' | 'Completed' | 'Flagged';

/**
 * Inspection Item Status
 * Represents the status of an individual inspection item
 */
export type InspectionItemStatus = 'pass' | 'fail' | 'na';

/**
 * Vendor Status
 * Represents the current state of a vendor account
 */
export type VendorStatus = 'Active' | 'Pending' | 'Suspended';

// ============================================================================
// Accounting Statuses
// ============================================================================

/**
 * Bill Status
 * Represents the current state of a bill/vendor invoice
 */
export type BillStatus = 'Pending Approval' | 'Approved' | 'Paid' | 'Overdue';

/**
 * Receivable Status
 * Represents the current state of a tenant receivable
 */
export type ReceivableStatus =
  | 'Paid'
  | 'PartiallyPaid'
  | 'Processing'
  | 'Late'
  | 'Unpaid'
  | 'Pending'
  | 'Disputed'
  | 'WrittenOff'
  /** Obligation removed before collection (e.g. lease termination voided future rent). */
  | 'Void';

/**
 * Payment Status
 * Represents the current state of a payment transaction
 */
export type PaymentStatus =
  | 'Pending'
  | 'Processing'
  | 'Completed'
  | 'Cleared'
  | 'Paid'
  | 'Failed'
  | 'Refunded'
  | 'Cancelled'
  /** Stripe capture succeeded but allocations exceed remaining AR (e.g. double checkout); PM resolves manually. */
  | 'Requires_Review';

/**
 * Purchase Order Status
 * Represents the current state of a purchase order
 */
export type PurchaseOrderStatus = 'draft' | 'sent' | 'acknowledged' | 'received' | 'cancelled';

/**
 * Three-Way Match Status
 * Represents the matching status of PO, invoice, and receipt
 */
export type ThreeWayMatchStatus = 'pending' | 'matched' | 'discrepancy' | 'approved';

/**
 * Payment Plan Status
 * Represents the current state of a payment plan
 */
export type PaymentPlanStatus = 'active' | 'completed' | 'defaulted' | 'cancelled';

/**
 * Installment Status
 * Represents the current state of a payment plan installment
 */
export type InstallmentStatus = 'pending' | 'paid' | 'overdue' | 'skipped';

/**
 * Vendor Invoice Status
 * Represents the current state of a vendor invoice
 */
export type VendorInvoiceStatus = 'Pending' | 'Paid' | 'Overdue';

// ============================================================================
// Leasing Statuses
// ============================================================================

/**
 * Application Status
 * Represents the current state of a rental application
 */
export type ApplicationStatus = 'Screening' | 'Approved' | 'Approved - Pending Lease' | 'Conditional' | 'Denied' | 'Pending' | 'Under Review' | 'Pending Docs' | 'Rejected';

/**
 * Lead Status
 * Represents the current state of a marketing lead
 */
export type LeadStatus = 'New Inquiry' | 'Contacted' | 'Tour Scheduled' | 'Tour Completed' | 'Application Sent' | 'Leased' | 'Lost';

/**
 * Listing Status
 * Represents the current state of a property listing
 */
export type ListingStatus = 'Active' | 'Draft' | 'Pending' | 'Leased';

/**
 * Background Check Status
 * Represents the current state of a background check request
 */
export type BackgroundCheckStatus = 'not_requested' | 'pending' | 'in_progress' | 'completed' | 'failed';

// ============================================================================
// E-Signature Statuses
// ============================================================================

/**
 * E-Signature Status
 * Represents the current state of an e-signature envelope
 */
export type ESignatureStatus = 'Draft' | 'Sent' | 'Delivered' | 'Signed' | 'Declined' | 'Voided';

// ============================================================================
// Investment Statuses
// ============================================================================

/**
 * Investment Entity Status
 * Represents the current state of an investment entity
 */
export type InvestmentEntityStatus = 'active' | 'closed' | 'forming';

/**
 * Investment Program Status
 * Represents the current state of an investment program
 */
export type InvestmentProgramStatus = 'Open' | 'Closed' | 'Coming Soon';

/**
 * Investor Status
 * Represents the current state of an investor account
 */
export type InvestorStatus = 'Active' | 'Prospective';

// ============================================================================
// Compliance Statuses
// ============================================================================

/**
 * HCV Program Inspection Status
 * Represents the inspection status for HCV/HUD programs
 */
export type HCVInspectionStatus = 'Passed' | 'Failed' | 'Pending' | 'Scheduled';

/**
 * Compliance Status
 * Represents the compliance status for affordable housing programs
 */
export type ComplianceStatus = 'Compliant' | 'At Risk' | 'Non-Compliant';

// ============================================================================
// Workflow Statuses
// ============================================================================

/**
 * Workflow Status
 * Represents the current state of a workflow
 */
export type WorkflowStatus = 'Active' | 'Paused';

/**
 * Workflow Execution Status
 * Represents the execution status of a workflow step
 */
export type WorkflowExecutionStatus = 'Success' | 'Failed' | 'Skipped';

// ============================================================================
// Project Statuses
// ============================================================================

/**
 * Project Status
 * Represents the current state of a project
 */
export type ProjectStatus = 'Pending' | 'In Progress' | 'Completed';

/**
 * Project Task Status
 * Represents the current state of a project task
 */
export type ProjectTaskStatus = 'Not Started' | 'In Progress' | 'Completed' | 'Approved';

/**
 * Project Phase Status
 * Represents the current state of a project phase
 */
export type ProjectPhaseStatus = 'Planning' | 'In Progress' | 'Completed' | 'On Hold' | 'Rejected';

// ============================================================================
// Integration Statuses
// ============================================================================

/**
 * DocuSign Status
 * Represents the status of a DocuSign envelope
 */
export type DocuSignStatus = 'created' | 'sent';

/**
 * SendGrid Status
 * Represents the delivery status of an email
 */
export type SendGridStatus = 'sent' | 'delivered' | 'bounced' | 'dropped' | 'deferred' | 'failed';

// ============================================================================
// System Statuses
// ============================================================================

/**
 * Activity Log Status
 * Represents the status/severity of an activity log entry
 */
export type ActivityLogStatus = 'Success' | 'Warning' | 'Error' | 'Info';

// ============================================================================
// Status Type Guards
// ============================================================================

/**
 * Type guard to check if a string is a valid LeaseStatus
 */
/** Canonical lease statuses (legacy `Pending Signature` maps to `Out_For_Signature`). */
export const CANONICAL_LEASE_STATUSES: readonly LeaseStatus[] = [
  'Draft',
  'Out_For_Signature',
  'Executed',
  'Active',
  'Expired',
  'Terminated',
  'Renewed',
] as const;

export function normalizeLeaseStatus(value: string): LeaseStatus {
  if (value === 'Pending Signature') return 'Out_For_Signature';
  if ((CANONICAL_LEASE_STATUSES as readonly string[]).includes(value)) {
    return value as LeaseStatus;
  }
  return 'Draft';
}

export function isLeaseStatus(value: string): value is LeaseStatus {
  return (CANONICAL_LEASE_STATUSES as readonly string[]).includes(value);
}

/**
 * Type guard to check if a string is a valid PropertyStatus
 */
export function isPropertyStatus(value: string): value is PropertyStatus {
  return ['Stable', 'At Risk', 'Lease-up', 'Renovation', 'Vacant'].includes(value);
}

/**
 * Type guard to check if a string is a valid TenantStatus
 */
export function isTenantStatus(value: string): value is TenantStatus {
  return ['Current', 'Notice', 'Past'].includes(value);
}

/**
 * Type guard to check if a string is a valid WorkOrderStatus
 */
export function isWorkOrderStatus(value: string): value is WorkOrderStatus {
  return [...TICKET_STATUS_ACTIVE, ...TICKET_STATUS_COMPLETED].includes(value as WorkOrderStatus);
}

/**
 * Type guard to check if a string is a valid BillStatus
 */
export function isBillStatus(value: string): value is BillStatus {
  return ['Pending Approval', 'Approved', 'Paid', 'Overdue'].includes(value);
}

/**
 * Type guard to check if a string is a valid ApplicationStatus
 */
export function isApplicationStatus(value: string): value is ApplicationStatus {
  return ['Screening', 'Approved', 'Approved - Pending Lease', 'Conditional', 'Denied', 'Pending', 'Under Review', 'Pending Docs', 'Rejected'].includes(value);
}
